import { formatDistanceToNow, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { Building, CreditCard } from "@/assets/icons";
import { Badge, Card } from "@/atoms";
import { BadgeStateEnum } from "@/enums";
import { AlfredAccountDataInterface } from "@/interfaces";
import { formatCurrencyAmountHandler, generateValidClassNameHandler } from "@/lib/utils";
import { StatusType } from "@/types";
import { Button } from "@/ui/button";

const getStatusBadgeState = (status: StatusType) => {
    switch (status) {
    case "ACTIVE":
        return BadgeStateEnum.SUCCESS;
    case "INACTIVE":
        return BadgeStateEnum.LIGHT;
    default:
        return BadgeStateEnum.DEFAULT;
    }
};

const formatLastActivity = (dateStr?: string) => {
    if (!dateStr) return "No activity";
    try {
        return formatDistanceToNow(
            parseISO(dateStr),
            { addSuffix: true },
        );
    } catch {
        return dateStr;
    }
};

export const AlfredAccountsSummaryCard = ({
    className,
    data,
} : {
    className?: string,
    data: AlfredAccountDataInterface,
}) => {
    const navigate = useNavigate();

    const handleViewAccount = () => {
        navigate(`/accounts/${data.id}`);
    };

    const handleShowTransactions = () => {
        navigate(`/ledger?account=${data.id}`);
    };

    const isAsset = data.category.toLowerCase().includes("asset");
    const isInactive = data.status === "INACTIVE";

    const headerContent = (
        <div className="flex items-start gap-2">
            {isAsset ? (
                <Building className="size-5 text-primary" />
            ) : (
                <CreditCard className="size-5 text-orange-500" />
            )}
            <div className="flex-1">
                <span className="text-sm font-medium leading-tight">
                    {data.name}
                </span>
                <p className="text-xs text-gray-500">{data.category}</p>
            </div>
        </div>
    );

    const cardContent = (
        <>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Balance</span>
                    <span className="text-lg font-semibold">
                        {formatCurrencyAmountHandler(
                            data.balance,
                            data.currency,
                        )}
                        <span className="ml-1 text-xs font-normal text-gray-500">{data.currency}</span>
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Type</span>
                    <span className="text-sm">
                        {data.balanceType === "DEBIT" ? "Debit Normal" : "Credit Normal"}
                    </span>
                </div>

                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Status</span>
                    <Badge state={getStatusBadgeState(data.status)}>
                        {data.status}
                    </Badge>
                </div>

                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Last Activity</span>
                    <span className="text-sm text-gray-600">
                        {formatLastActivity(data.lastActivity)}
                    </span>
                </div>

                {data.activityData && data.activityData.length > 0 && (
                    <div className="pt-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">7-day trend</span>
                        </div>
                        <div className="mt-1 flex h-8 items-end gap-0.5">
                            {data.activityData.slice(-7).map((value, idx) => {
                                const max = Math.max(...data.activityData!);
                                const height = max > 0 ? (value / max) * 100 : 0;
                                return (
                                    <div
                                        className="bg-primary/20 flex-1 rounded-t"
                                        key={idx} // eslint-disable-line
                                        style={{
                                            height: `${Math.max(
                                                height,
                                                5,
                                            )}%`,
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Action buttons as part of content (Option 3) */}
            <div className="mt-3 flex flex-col gap-2 border-t pt-3 sm:flex-row">
                <Button
                    className="w-full sm:flex-1"
                    size="sm"
                    onClick={handleViewAccount}
                >
                    View Account
                </Button>
                <Button
                    className="w-full sm:flex-1"
                    size="sm"
                    variant="outline"
                    onClick={handleShowTransactions}
                >
                    Show Txns
                </Button>
            </div>
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-3 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full border shadow-sm",
                isInactive && "opacity-75",
                className,
            )}
        />
    );
};
